import React, { useMemo, useRef, useState } from 'react';
import {
  Coffee, Croissant, MapPin, Package, PlusCircle, RotateCcw, ShoppingBag,
  Utensils, UtensilsCrossed, Wine, X,
} from 'lucide-react';
import { fixtureGlyphs } from '../../lib/fixtureEmoji';

/* BuffetFloorPlanner: tables MOVE and dishes get a PLACE, saved on the buffet rather than the branch's plan. */

/* The dish list is grouped and tinted by ITEM TYPE, in the same order and
   palette as the menu catalogue's "Show Only" filter and type badges — a
   colour learned on the Dishes screen means the same thing here. Amber stays
   this planner's own accent for "on the floor" (moved tables, stations), so
   placement is marked with a ring and a pin, never by re-tinting the chip. */
const DISH_TYPES = {
  dish: { label: 'Dishes', Icon: UtensilsCrossed, chip: 'border-rose-200 bg-rose-50 text-rose-900', icon: 'text-rose-500', head: 'text-rose-600' },
  beverage: { label: 'Beverages', Icon: Coffee, chip: 'border-sky-200 bg-sky-50 text-sky-900', icon: 'text-sky-500', head: 'text-sky-600' },
  addon: { label: 'Add-ons', Icon: PlusCircle, chip: 'border-emerald-200 bg-emerald-50 text-emerald-900', icon: 'text-emerald-500', head: 'text-emerald-600' },
  combo: { label: 'Combos', Icon: Package, chip: 'border-violet-200 bg-violet-50 text-violet-900', icon: 'text-violet-500', head: 'text-violet-600' },
  store_bought: { label: 'Store-bought', Icon: ShoppingBag, chip: 'border-amber-200 bg-amber-50 text-amber-900', icon: 'text-amber-600', head: 'text-amber-600' },
  bakery: { label: 'Bakery', Icon: Croissant, chip: 'border-orange-200 bg-orange-50 text-orange-900', icon: 'text-orange-500', head: 'text-orange-600' },
  liquor: { label: 'Liquor', Icon: Wine, chip: 'border-purple-200 bg-purple-50 text-purple-900', icon: 'text-purple-500', head: 'text-purple-600' },
  /* A name the catalogue no longer answers for (renamed or deleted dish) still
     has to be placeable, so it gets a neutral bucket rather than vanishing. */
  other: { label: 'Other', Icon: Utensils, chip: 'border-border bg-white text-slate-700', icon: 'text-slate-400', head: 'text-slate-500' },
};
const TYPE_ORDER = ['dish', 'beverage', 'addon', 'combo', 'store_bought', 'bakery', 'liquor', 'other'];

const CHAIR_GAP = 28;
const GRID = 10;
/* Stations are small on purpose: a label saying where the biryani goes, not a drawing of the chafing dish. */
const STATION_W = 112;
const STATION_H = 44;
const STATION_GAP = 16;
/* Breathing room drawn OUTSIDE the room's bounds, so a chair belonging to a
   table shoved against the wall is not sliced clean off. Deliberately SHORT
   of the full 22px a chair overhangs by: this gutter is drawn on all four
   sides, and at any width worth noticing it reads as a band of empty space
   framing the plan. A chair at the very wall loses a few pixels; the drawing
   gains a border that sits against it. */
const PAD = 8;
/* The dish list's column, subtracted from the width the plan may use. Kept
   next to the lg:w-[210px] that draws it — they have to agree. */
const PALETTE_W = 210;
/* How far a room may be stretched for one buffet — generous, but short of the ten-metre typo. */
const MAX_CANVAS = 4000;

const FIXTURE_TINT = {
  kitchen: '#fde68a', washroom: '#bae6fd', stairs: '#e2e8f0', plant: '#bbf7d0',
  door: '#ddd6fe', window: '#dbeafe', column: '#cbd5e1', reception: '#fbcfe8',
  bar: '#fed7aa', wall: '#e2e8f0',
};

const snap = (v) => Math.round(v / GRID) * GRID;
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

/* Chair placement copied from SeatingFloorView, so the same table is the same picture everywhere. */
function chairsAround(t) {
  const n = Math.max(0, t.capacity || 0);
  if (n === 0) return [];
  const w = t.width || 100;
  const h = t.height || 70;
  const cx = CHAIR_GAP + w / 2;
  const cy = CHAIR_GAP + h / 2;
  if (t.shape === 'round') {
    const r = Math.max(w, h) / 2 + 14;
    return Array.from({ length: n }, (_, i) => {
      const a = (i / n) * Math.PI * 2 - Math.PI / 2;
      return { left: cx + r * Math.cos(a) - 8, top: cy + r * Math.sin(a) - 8 };
    });
  }
  const edges = ['top', 'right', 'bottom', 'left'];
  const placements = Array.from({ length: n }, (_, i) => edges[i % 4]);
  const perEdge = { top: 0, right: 0, bottom: 0, left: 0 };
  placements.forEach((e) => { perEdge[e] += 1; });
  const seen = { top: 0, right: 0, bottom: 0, left: 0 };
  return placements.map((edge) => {
    const fraction = (seen[edge]++ + 1) / (perEdge[edge] + 1);
    if (edge === 'top') return { left: CHAIR_GAP + w * fraction - 8, top: CHAIR_GAP - 22 };
    if (edge === 'bottom') return { left: CHAIR_GAP + w * fraction - 8, top: CHAIR_GAP + h + 6 };
    if (edge === 'left') return { left: CHAIR_GAP - 22, top: CHAIR_GAP + h * fraction - 8 };
    return { left: CHAIR_GAP + w + 6, top: CHAIR_GAP + h * fraction - 8 };
  });
}

function tableLabel(t) {
  return t.table_code || `#${t.id}`;
}

/* A drag reporting movement in CANVAS units: without dividing by the scale every drag would crawl. */
function useDrag(scaleRef, onMove) {
  return (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const scale = scaleRef.current || 1;
    const move = (ev) => onMove(
      (ev.clientX - startX) / scale,
      (ev.clientY - startY) / scale,
    );
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };
}

/* Module-level, not inside the page: a component declared in a render remounts and the drag dies mid-gesture. */
function TableNode({ item, x, y, scaleRef, onMove, moved }) {
  const w = item.width || 100;
  const h = item.height || 70;
  const start = useRef({ x, y });
  const onMouseDown = useDrag(scaleRef, (dx, dy) => {
    onMove(snap(start.current.x + dx), snap(start.current.y + dy));
  });

  return (
    <div
      role="button"
      tabIndex={-1}
      onMouseDown={(e) => { start.current = { x, y }; onMouseDown(e); }}
      className="absolute z-10 cursor-move select-none"
      style={{
        left: x - CHAIR_GAP,
        top: y - CHAIR_GAP,
        width: w + CHAIR_GAP * 2,
        height: h + CHAIR_GAP * 2,
        transform: item.rotation ? `rotate(${item.rotation}deg)` : undefined,
      }}
      title={`${tableLabel(item)} · ${item.capacity || 0} seats — drag to move`}
    >
      {chairsAround(item).map((c, i) => (
        <div key={i} className="absolute rounded-md"
          style={{ left: c.left, top: c.top, width: 16, height: 16,
            background: '#cbd5e1', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
      ))}
      <div
        className={`absolute flex flex-col items-center justify-center font-semibold text-white ${
          moved ? 'ring-2 ring-amber-400' : ''}`}
        style={{
          left: CHAIR_GAP,
          top: CHAIR_GAP,
          width: w,
          height: h,
          background: 'linear-gradient(135deg,#059669,#047857)',
          borderRadius: item.shape === 'round' ? '50%' : 8,
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
          fontSize: Math.min(14, h * 0.24),
        }}
      >
        <span>{tableLabel(item)}</span>
        <span className="text-[10px] font-normal opacity-90">{item.capacity || 0} seats</span>
      </div>
    </div>
  );
}

function StationNode({ st, scaleRef, onMove, onRemove, onSetNum }) {
  const start = useRef({ x: st.x, y: st.y });
  const onMouseDown = useDrag(scaleRef, (dx, dy) => {
    onMove(snap(start.current.x + dx), snap(start.current.y + dy));
  });
  /* The number is typed over in place; committed on blur/Enter, and an empty
     or zero entry reverts rather than leaving a counter with no name. */
  const [numDraft, setNumDraft] = useState(null);
  const commitNum = () => {
    const n = parseInt(numDraft, 10);
    if (Number.isInteger(n) && n > 0 && n !== st.num) onSetNum(n);
    setNumDraft(null);
  };

  return (
    <div
      onMouseDown={(e) => { start.current = { x: st.x, y: st.y }; onMouseDown(e); }}
      className="group absolute z-20 cursor-move select-none rounded-md border-2 border-amber-500 bg-amber-100/95 shadow-sm"
      style={{ left: st.x, top: st.y, width: STATION_W, height: STATION_H }}
      title={`C${st.num} · ${st.dish} — drag to move; type over the number to renumber the counter`}
    >
      {/* The counter's NUMBER, worn on the box the way a table wears T7 — it is
          what the batch log and the floor say out loud ("more biryani on C3").
          Typed over in place; the drag is stopped so editing never moves it. */}
      <span
        onMouseDown={(e) => e.stopPropagation()}
        className="absolute -left-2 -top-2 flex items-center rounded-full border border-amber-500 bg-white pl-1 pr-0.5 text-[10px] font-bold text-amber-800 shadow-sm"
      >
        C
        <input
          type="text"
          inputMode="numeric"
          value={numDraft ?? String(st.num ?? '')}
          onFocus={(e) => { setNumDraft(String(st.num ?? '')); e.target.select(); }}
          onChange={(e) => setNumDraft(e.target.value.replace(/\D+/g, '').slice(0, 3))}
          onBlur={commitNum}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          aria-label={`Counter number for ${st.dish}`}
          className="w-5 border-0 bg-transparent p-0 text-center text-[10px] font-bold text-amber-800 focus:outline-none"
        />
      </span>
      {/* The name gets the whole box with the icon inline; two lines then a clip, with the full name on the tooltip. */}
      <div className="flex h-full items-center justify-center gap-1 px-1.5 text-center">
        <Utensils size={9} className="shrink-0 text-amber-700" />
        <span className="text-[10px] font-semibold leading-[1.15] text-amber-900 line-clamp-2">
          {st.dish}
        </span>
      </div>
      <button
        type="button"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="absolute -right-1.5 -top-1.5 hidden h-4 w-4 items-center justify-center rounded-full border border-red-300 bg-white text-red-600 shadow group-hover:flex"
        aria-label={`Remove ${st.dish} from the floor`}
      >
        <X size={9} />
      </button>
    </div>
  );
}

/**
 * @param {boolean} readOnly  Mini mode: the plan is a PREVIEW. Nothing drags,
 *   no dish can be placed. Editing a plan in a column a few
 *   hundred pixels wide produced accidental drags nobody meant, so the small
 *   copy shows the arrangement and the expanded one is where it is changed.
 */
export default function BuffetFloorPlanner({
  areas, dishes, dishTypes = {}, layout, onChange, readOnly = false,
  /* Set by the full-screen editor, which owns the whole viewport and so can
     be fitted to its height. The wizard's preview scrolls with the page and
     must keep sizing itself on width alone. */
  fitHeight = false,
}) {
  const [activeId, setActiveId] = useState(() => (areas[0] ? String(areas[0].id) : null));
  const scaleRef = useRef(1);
  const rootRef = useRef(null);
  /* The panel's own box, not the window's: the fit needs the room column's
     width AND how much screen is left below it, or a board that fits the
     width perfectly still runs off the bottom edge. */
  const [box, setBox] = useState({ w: 0, h: 0, wide: false });

  // The picked area on screen, falling back to the first still picked rather than a blank canvas.
  const active = useMemo(
    () => areas.find((a) => String(a.id) === String(activeId)) || areas[0] || null,
    [areas, activeId],
  );

  React.useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    /* Matches the lg: breakpoint the dish list stacks at — below it the list
       sits UNDER the plan, so its 210px must not come off the width. */
    const mq = window.matchMedia('(min-width: 1024px)');
    const read = () => setBox({
      w: el.clientWidth,
      /* From the panel's top edge to the bottom of the window, less the area
         tabs, the caption under the plan and a little air. */
      h: Math.max(260, window.innerHeight - el.getBoundingClientRect().top - 104),
      wide: mq.matches,
    });
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    window.addEventListener('resize', read);
    mq.addEventListener('change', read);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', read);
      mq.removeEventListener('change', read);
    };
  }, []);

  if (!active) return null;

  const secId = String(active.id);
  const secLayout = layout[secId] || {};
  const movedTables = secLayout.tables || {};
  const stations = secLayout.stations || [];

  /* The room. The branch's own plan sets the size — a real room never
     shrinks below itself. It is no longer stretchable here: the grip that
     dragged the corner is gone, so this only READS a canvas a plan saved
     while it existed, and the "Original size" reset below puts such a plan
     back to the branch's own walls. */
  const baseW = active.canvas_width || 1200;
  const baseH = active.canvas_height || 800;
  const stretchSpec = secLayout.canvas || {};
  const cw = clamp(Number(stretchSpec.w) || baseW, baseW, MAX_CANVAS);
  const ch = clamp(Number(stretchSpec.h) || baseH, baseH, MAX_CANVAS);
  const stretched = cw > baseW || ch > baseH;
  const boardW = cw + PAD * 2;
  const boardH = ch + PAD * 2;
  /* What the row can spare: the dish list beside it, the room column's p-1
     and the list's p-2, and the glass panel's own inset - 44 all told, so the
     board never lands a pixel wider than the panel and gets clipped by it.
     The list is paid for ONCE. It used to be counted twice, the second time
     for a phantom gutter opposite it that pushed the panel off to the right
     by its own width; the panel hugs its contents now, so centring it is
     margin: auto's job and costs no screen at all. */
  const availW = Math.max(240, box.w - (box.wide ? PALETTE_W : 0) - 44);
  /* Fit the whole board, height included — a plan you have to scroll to see
     is not a plan. Scaling PAST 1:1 is allowed now: refusing to grow left a
     short room sitting in a third of a screen of nothing. The 1.6 ceiling
     stops a two-table room from becoming a cartoon. */
  const scale = box.w > 0
    ? (fitHeight
      ? clamp(Math.min(availW / boardW, box.h / boardH), 0.15, 1.6)
      : Math.min(1, availW / boardW))
    : 1;
  scaleRef.current = scale;

  const patch = (next) => {
    // Preview mode changes nothing. Gated at the single write point rather
    // than on each handler, so no future control can bypass it by accident.
    if (readOnly) return;
    onChange({ ...layout, [secId]: { ...secLayout, ...next } });
  };

  const posOf = (t) => movedTables[String(t.id)] || { x: t.x ?? 100, y: t.y ?? 100 };

  const moveTable = (t, x, y) => patch({
    tables: {
      ...movedTables,
      [String(t.id)]: {
        x: clamp(x, 0, Math.max(0, cw - (t.width || 100))),
        y: clamp(y, 0, Math.max(0, ch - (t.height || 70))),
      },
    },
  });

  const resetTables = () => patch({ tables: {} });

  const moveStation = (id, x, y) => patch({
    stations: stations.map((s) => (s.id === id
      ? { ...s, x: clamp(x, 0, Math.max(0, cw - STATION_W)),
        y: clamp(y, 0, Math.max(0, ch - STATION_H)) }
      : s)),
  });

  const removeStation = (id) => patch({ stations: stations.filter((s) => s.id !== id) });

  /* Renumber one counter by hand. Nothing else is renumbered for it: the
     numbers are the user's to arrange, duplicates and all. */
  const setStationNum = (id, num) => patch({
    stations: stations.map((s) => (s.id === id ? { ...s, num } : s)),
  });

  /* Counter numbers run across the WHOLE buffet, not per room — service says
     "counter 3", never "the third counter of the patio" — so the next free
     number is looked for over every area's stations. */
  const nextCounterNum = () => {
    const used = new Set(
      Object.values(layout).flatMap((l) => (l?.stations || [])
        .map((s) => Number(s?.num)).filter((n) => Number.isInteger(n) && n > 0)),
    );
    let n = 1;
    while (used.has(n)) n += 1;
    return n;
  };

  /* A newly placed dish lands on a free-ish slot, since stacked stations look like one and drag wrongly.
     Placing a dish that is already on the floor is deliberate — the second pan gets its OWN counter number. */
  const addStation = (dish) => {
    const n = stations.length;
    const step = STATION_W + STATION_GAP;
    const perRow = Math.max(1, Math.floor((cw - STATION_GAP) / step));
    /* Size is not stored: it is how a station is DRAWN, so every saved buffet follows the current size. */
    patch({
      stations: [...stations, {
        id: `st_${dish.replace(/\W+/g, '_').slice(0, 24)}_${n}_${Date.now().toString(36)}`,
        dish,
        num: nextCounterNum(),
        x: snap(STATION_GAP + (n % perRow) * step),
        y: snap(STATION_GAP + Math.floor(n / perRow) * (STATION_H + STATION_GAP)),
      }],
    });
  };

  const placedHere = new Set(stations.map((s) => s.dish));
  const placedAnywhere = new Set(
    Object.values(layout).flatMap((l) => (l.stations || []).map((s) => s.dish)),
  );
  const tables = active.tables || [];
  const fixtures = active.fixtures || [];
  const movedCount = Object.keys(movedTables).length;

  return (
    /* Full width and invisible, existing only to MEASURE: the panel inside it
       is then free to shrink to the plan and sit centred, instead of spanning
       the screen with the drawing marooned in the middle of it. */
    <div ref={rootRef}>
    {/* Sized to what is in it, both ways round, and centred by margin. The
        expanded editor used to span the whole row with the drawing marooned
        in the middle of it and a band of dead screen down each side; now the
        panel ends where the plan and its dish list end.

        Glass rather than a plain card, because this is a floating editor over
        the studio: a translucent ground with the page blurred behind it, a
        light top edge for the lift, and a wide soft shadow to sit it above
        the page. Held to the theme's own tokens (`background`, `border`) at
        low alpha, so it is the same panel in dark mode and under every accent
        the product ships. */}
    <div className={`mx-auto max-w-full w-fit ${fitHeight
      ? 'overflow-hidden rounded-2xl border border-white/70 bg-card/70 shadow-[0_18px_50px_-20px_rgba(15,23,42,0.35)] ring-1 ring-border/60 backdrop-blur-xl dark:border-white/10 dark:bg-card/50 dark:ring-white/10'
      : 'rounded-lg border border-border'}`}>
      {/* One tab per picked area: a single canvas spanning two floors would be a drawing of nowhere. */}
      {areas.length > 1 && (
        <div className="flex flex-wrap gap-1 border-b border-border/60 bg-muted/30 px-2 py-1.5 backdrop-blur-sm">
          {areas.map((a) => {
            const on = String(a.id) === secId;
            const n = (layout[String(a.id)]?.stations || []).length;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => setActiveId(String(a.id))}
                className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium ${
                  on ? 'bg-primary text-primary-foreground' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                <MapPin size={12} /> {a.name}
                {n > 0 && <span className={on ? 'opacity-80' : 'text-muted-foreground'}>· {n}</span>}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-center">
        {/* ---- the room ---- */}
        {/* p-1, not p-2: the panel's border should sit against the plan, and
            this padding stacks on top of PAD to frame it twice over. */}
        <div className="min-w-0 p-1">
          {/* Sized to the DRAWING, so the column shrinks to it and the dish
              list comes to meet it. The space a room does not need is then a
              margin around the pair instead of a hole beside the plan. */}
          <div className={`flex justify-center overflow-hidden ${readOnly ? "[&_*]:!cursor-default" : ""}`}
            style={{ width: boardW * scale, maxWidth: '100%' }}>
            {/* An inset hairline rather than a border: the room is inside the
                glass, so its edge should read as a pane within the panel and
                not as a second frame drawn around the first. */}
            <div className="overflow-hidden rounded-xl bg-muted/30 ring-1 ring-inset ring-border/40"
              style={{ width: boardW * scale, height: boardH * scale }}>
              {/* Outer: the room PLUS its margin, carrying the scale and ground so the padding reads as part of the drawing. */}
              <div
                className="relative origin-top-left rounded-md bg-white"
                style={{
                  width: cw + PAD * 2,
                  height: ch + PAD * 2,
                  transform: `scale(${scale})`,
                }}
              >
                {/* Inner: the room's real bounds, offset by the margin, with overflow visible so a chair may reach past the wall. */}
                <div
                  className="absolute border border-dashed border-border"
                  style={{
                    left: PAD,
                    top: PAD,
                    width: cw,
                    height: ch,
                    backgroundImage: [
                      'repeating-linear-gradient(0deg, hsl(var(--border)) 0 1px, transparent 1px 40px)',
                      'repeating-linear-gradient(90deg, hsl(var(--border)) 0 1px, transparent 1px 40px)',
                    ].join(', '),
                  }}
                >
                {/* Fixtures are context and never draggable: the kitchen door is
                    where the building put it. Each wears its emoji — 🪴, 🚪, 🪟 —
                    repeated along a strip so a window lining a wall reads as
                    panes; the same face every plan renderer shows. */}
                {fixtures.map((f) => {
                  const fw = f.width || 100;
                  const fh = f.height || 80;
                  const glyphs = fixtureGlyphs(f.fixture_type, fw, fh);
                  const verticalFx = fh > fw;
                  const thinFx = Math.max(1, Math.min(fw, fh));
                  return (
                    <div
                      key={`fx-${f.id}`}
                      className={`absolute flex ${verticalFx ? 'flex-col' : 'flex-row'} select-none items-center justify-center gap-0.5 overflow-hidden rounded-md text-[10px] text-slate-600`}
                      style={{
                        left: f.x ?? 100,
                        top: f.y ?? 100,
                        width: fw,
                        height: fh,
                        background: f.color || FIXTURE_TINT[f.fixture_type] || '#e2e8f0',
                        transform: f.rotation ? `rotate(${f.rotation}deg)` : undefined,
                        opacity: 0.7,
                      }}
                      title={f.label || f.fixture_type}
                    >
                      {glyphs.map((g, i) => (
                        <span key={i} aria-hidden="true" className="leading-none"
                          style={{ fontSize: glyphs.length > 1
                            ? Math.min(20, Math.max(10, Math.round(thinFx * 0.75)))
                            : Math.min(24, Math.max(12, Math.round(thinFx * 0.5))) }}>
                          {g}
                        </span>
                      ))}
                      {Math.min(fw, fh) > 30 && <span>{f.label || f.fixture_type}</span>}
                    </div>
                  );
                })}

                {tables.map((t) => {
                  const p = posOf(t);
                  return (
                    <TableNode
                      key={t.id}
                      item={t}
                      x={p.x}
                      y={p.y}
                      scaleRef={scaleRef}
                      moved={Boolean(movedTables[String(t.id)])}
                      onMove={(x, y) => moveTable(t, x, y)}
                    />
                  );
                })}

                {stations.map((st) => (
                  <StationNode
                    key={st.id}
                    st={st}
                    scaleRef={scaleRef}
                    onMove={(x, y) => moveStation(st.id, x, y)}
                    onRemove={() => removeStation(st.id)}
                    onSetNum={(num) => setStationNum(st.id, num)}
                  />
                ))}

                {tables.length === 0 && fixtures.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                    Nothing drawn in this area yet.
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-xs text-muted-foreground"
            style={{ width: boardW * scale, maxWidth: '100%' }}>
            <span>{tables.length} tables · {stations.length} dish station{stations.length === 1 ? '' : 's'}</span>
            {movedCount > 0 && (
              <>
                <span className="text-amber-600">{movedCount} table{movedCount === 1 ? '' : 's'} moved for this buffet</span>
                <button type="button" onClick={resetTables}
                  className="inline-flex items-center gap-1 text-primary hover:underline">
                  <RotateCcw size={11} /> Put them back
                </button>
              </>
            )}
            {stretched && (
              <>
                <span className="text-amber-600">
                  room stretched to {cw} × {ch} for this buffet
                </span>
                <button type="button" onClick={() => patch({ canvas: null })}
                  title={`Back to the branch's own ${baseW} × ${baseH}. Anything standing beyond the original walls stays put — drag it back in.`}
                  className="inline-flex items-center gap-1 text-primary hover:underline">
                  <RotateCcw size={11} /> Original size
                </button>
              </>
            )}
          </div>
        </div>

        {/* ---- the dishes waiting for a place ---- */}
        <div className="shrink-0 border-t border-border/60 p-2 lg:w-[210px] lg:border-l lg:border-t-0">
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Dishes
          </div>
          {dishes.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No dishes yet — pick a buffet menu and they appear here.
            </p>
          ) : (
            <div className="max-h-[420px] space-y-2 overflow-auto pr-1">
              {TYPE_ORDER.map((key) => {
                const group = dishes.filter((d) => {
                  const t = dishTypes[(d || '').trim().toLowerCase()];
                  return (DISH_TYPES[t] ? t : 'other') === key;
                });
                if (group.length === 0) return null;
                const { label, Icon: TypeIcon, icon, head, chip } = DISH_TYPES[key];
                return (
                  <div key={key}>
                    <div className={`mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${head}`}>
                      <TypeIcon size={10} /> {label}
                      <span className="font-normal opacity-70">· {group.length}</span>
                    </div>
                    <div className="space-y-1">
                      {group.map((d) => {
                        const here = placedHere.has(d);
                        const elsewhere = !here && placedAnywhere.has(d);
                        return (
                          <button
                            key={d}
                            type="button"
                            onClick={() => addStation(d)}
                            className={`flex w-full items-center gap-1.5 rounded-md border px-2 py-1.5 text-left text-xs hover:brightness-95 ${chip} ${
                              here ? 'ring-1 ring-amber-400' : ''}`}
                            title={here
                              ? `${d} is already on this floor — click to add a second station`
                              : `Place ${d} on the floor`}
                          >
                            <TypeIcon size={11} className={`shrink-0 ${icon}`} />
                            <span className="flex-1 truncate">{d}</span>
                            {/* Amber pin = standing on THIS floor, matching the moved-table and station accent. */}
                            {here && <MapPin size={10} className="shrink-0 text-amber-600" />}
                            {/* Placed in ANOTHER area: worth saying, or a dish looks unplaced on every tab but its own. */}
                            {elsewhere && <span className="shrink-0 text-[10px] text-muted-foreground">elsewhere</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
    </div>
  );
}
